import {keepPreviousData, useQuery, type QueryKey} from '@github-ui/react-query'
import {reactFetch} from '@github-ui/verified-fetch'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import type {AvailableReviewersData} from '../payloads/available-reviewers'

function availableReviewersQueryKey(apiUrl: string): QueryKey {
  return [PageData.availableReviewers, apiUrl]
}

/**
 * Loads the reviewer typeahead suggestions from
 * `GET /pull/:id/page_data/reviewers?q=<query>`. When `query` is empty, the
 * server still returns a capped initial set of users.
 *
 * Previous results are kept while a new request is in flight so the picker
 * doesn't flash to an empty state as the user types.
 */
export function useAvailableReviewers({query, enabled = true}: {query?: string; enabled?: boolean} = {}) {
  const params = query ? `q=${encodeURIComponent(query)}` : undefined
  const apiUrl = usePageDataUrl(PageData.availableReviewers, params)
  return useQuery<AvailableReviewersData>({
    queryKey: availableReviewersQueryKey(apiUrl),
    queryFn: async () => {
      const result = await reactFetch(apiUrl)
      if (!result.ok) throw new Error(`HTTP ${result.status}`)
      return (await result.json()) as AvailableReviewersData
    },
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  })
}
